import * as React from 'react';
import type {
  AdapterCapabilities,
  PacingOption
} from 'shared/src/domain/ai/skills/skill-platform-types';
import { resolveMediaPlanDefaults } from 'shared/src/domain/media-plan/resolve-media-plan-defaults';
import {
  seedObjectiveSplitRows,
  type MediaPlanCardSelection,
  type MediaPlanInferredFromZoho
} from './media-plan-configuration-card';

type LineItemForHydration = {
  planned_unit_cost: number;
  unit_price_type_name: string;
  geo?: string;
  tactic_name?: string;
  channel_name?: string;
};

type Props = {
  capabilities: AdapterCapabilities;
  /** Backend-derived prefill for the account-ID field (linked entity or dev/sandbox default). */
  accountIdPrefill?: string;
  lineItem: LineItemForHydration;
  zohoGeo: string[];
  zohoAudience: string[];
  zohoDescription?: string;
  zohoNotes?: string;
  /** Line-item price; seeds the objective-split rows' budget shares. */
  lineItemPrice?: number;
  hydrationKey: string;
  setSelection: React.Dispatch<React.SetStateAction<MediaPlanCardSelection>>;
  setInferredFromZoho: React.Dispatch<React.SetStateAction<MediaPlanInferredFromZoho>>;
  hydratedFor: React.MutableRefObject<string | null>;
};

export function ZohoMediaPlanHydrator({
  capabilities,
  accountIdPrefill,
  lineItem,
  zohoGeo,
  zohoAudience,
  zohoDescription,
  zohoNotes,
  lineItemPrice,
  hydrationKey,
  setSelection,
  setInferredFromZoho,
  hydratedFor
}: Props) {
  React.useEffect(() => {
    if (hydratedFor.current === hydrationKey) return;
    hydratedFor.current = hydrationKey;

    const intent = {
      geo: zohoGeo,
      audience: zohoAudience,
      description: zohoDescription,
      notes: zohoNotes
    };
    const resolved = resolveMediaPlanDefaults(lineItem, intent, capabilities);
    const { defaults } = resolved;
    // Debug: surface what the resolver saw and decided. Remove once auto-fill is verified.
    console.warn('[ZohoMediaPlanHydrator]', {
      lineItem,
      intent,
      lineItemTypes: capabilities.lineItemTypes,
      lineItemTypeAliases: capabilities.lineItemTypeAliases,
      defaults,
      ambiguities: resolved.ambiguities
    });

    const defaultBiddingStrategy =
      defaults.biddingStrategy ??
      (capabilities.biddingStrategies.length === 1 ? capabilities.biddingStrategies[0] : undefined);
    const defaultPacing: PacingOption | undefined = capabilities.pacingOptions?.includes('lifetime')
      ? 'lifetime'
      : capabilities.pacingOptions?.[0];
    const defaultBudgetType = capabilities.budgetTypes.includes('spend')
      ? 'spend'
      : capabilities.budgetTypes[0];

    setSelection(prev => ({
      accountId: prev.accountId ?? accountIdPrefill,
      managerAccountId: prev.managerAccountId ?? capabilities.managerAccounts?.[0]?.id,
      campaignMode: prev.campaignMode ?? 'create',
      campaignId: prev.campaignId,
      lineItemType: defaults.lineItemType ?? prev.lineItemType,
      budgetType: prev.budgetType ?? defaultBudgetType,
      biddingStrategy: prev.biddingStrategy ?? defaultBiddingStrategy,
      pacing: prev.pacing ?? defaultPacing,
      cpmBid: prev.cpmBid ?? defaults.bid,
      geo: defaults.geo ?? prev.geo,
      pageId: prev.pageId,
      specialAdCategories: prev.specialAdCategories,
      ageMin: prev.ageMin ?? capabilities.audience?.defaultAgeMin,
      campaigns: prev.campaigns ?? seedObjectiveSplitRows(capabilities, lineItemPrice)
    }));

    setInferredFromZoho({
      lineItemType: defaults.lineItemType !== undefined,
      geo: !!defaults.geo?.length
    });
  }, [
    hydrationKey,
    capabilities,
    accountIdPrefill,
    lineItem,
    zohoGeo,
    zohoAudience,
    zohoDescription,
    zohoNotes,
    lineItemPrice,
    setSelection,
    setInferredFromZoho,
    hydratedFor
  ]);

  return null;
}
